<template>
  <div class="max-w-3xl mx-auto py-8 px-4 sm:px-6">
    <div class="flex items-center justify-between mb-8">
      <div>
        <h1 class="text-2xl font-bold text-text-primary">Notifications</h1>
        <p v-if="notifications.length > 0" class="text-sm text-text-secondary mt-1">
          {{ totalCount }} notification{{ totalCount !== 1 ? 's' : '' }}
          <span v-if="unreadCount > 0" class="text-primary font-medium"> · {{ unreadCount }} unread</span>
        </p>
      </div>
      <button
          v-if="hasUnread"
          @click="markAllRead"
          class="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-primary bg-primary/10 hover:bg-primary/20 rounded-xl transition-all duration-200"
      >
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        Mark all read
      </button>
    </div>

    <!-- Browser notification permission banner -->
    <div
      v-if="browserNotif.isSupported() && browserNotif.permission() === 'default'"
      class="mb-6 px-5 py-4 bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20 border border-purple-200 dark:border-purple-800/30 rounded-2xl flex items-center gap-4"
    >
      <div class="p-2 rounded-xl bg-white dark:bg-background-light shadow-sm">
        <svg class="w-6 h-6 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
      </div>
      <div class="flex-1">
        <p class="text-sm font-medium text-text-primary">Stay notified even when you're away</p>
        <p class="text-xs text-text-secondary mt-0.5">Enable desktop notifications to receive alerts about your orders</p>
      </div>
      <button
        @click="enableDesktopNotifs"
        class="flex-shrink-0 px-4 py-2 text-sm font-semibold text-white bg-primary hover:bg-primary-dark rounded-xl transition-colors shadow-sm"
      >
        Enable
      </button>
    </div>

    <!-- Blocked permission banner -->
    <div
      v-if="browserNotif.isSupported() && browserNotif.permission() === 'denied'"
      class="mb-6 px-5 py-4 bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800/30 rounded-2xl flex items-center gap-4"
    >
      <div class="p-2 rounded-xl bg-white dark:bg-background-light shadow-sm">
        <svg class="w-6 h-6 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
        </svg>
      </div>
      <div class="flex-1">
        <p class="text-sm font-medium text-text-primary">Desktop notifications blocked</p>
        <p class="text-xs text-text-secondary mt-0.5">Please allow notifications in your browser settings to receive alerts.</p>
      </div>
    </div>

    <div v-if="loading && notifications.length === 0" class="flex items-center justify-center py-24">
      <svg class="animate-spin h-10 w-10 text-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
      </svg>
    </div>

    <div v-else-if="notifications.length === 0" class="text-center py-24">
      <div class="w-20 h-20 mx-auto mb-6 rounded-2xl bg-background-lighter flex items-center justify-center">
        <svg class="w-10 h-10 text-text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
      </div>
      <h3 class="text-lg font-semibold text-text-primary mb-1">All clear!</h3>
      <p class="text-text-secondary text-sm">You have no notifications at the moment.</p>
    </div>

    <template v-else>
      <TransitionGroup name="notif-list" tag="div" class="space-y-2">
        <div
            v-for="notif in notifications"
            :key="notif.id"
            @click="handleClick(notif)"
            :class="[
              'group relative flex items-start gap-4 p-4 sm:p-5 rounded-2xl cursor-pointer transition-all duration-200 border',
              notif.is_read
                ? 'bg-background border-background-lighter hover:border-background-lighter/80 hover:shadow-sm'
                : 'bg-gradient-to-r from-primary/[0.04] to-transparent border-primary/15 hover:border-primary/30 hover:shadow-md hover:shadow-primary/5'
            ]"
        >
          <div
              :class="[
                'flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center text-lg transition-colors duration-200',
                typeColors[notif.type]?.bg || 'bg-background-lighter',
                typeColors[notif.type]?.text || 'text-text-secondary'
              ]"
          >
            {{ notif.icon || '🔔' }}
          </div>

          <div class="flex-1 min-w-0 pt-0.5">
            <div class="flex items-start justify-between gap-3">
              <p :class="['text-sm leading-snug', notif.is_read ? 'text-text' : 'text-text font-semibold']">
                {{ notif.title }}
              </p>
              <div class="flex items-center gap-2 flex-shrink-0 mt-0.5">
                <span class="text-xs text-text-secondary whitespace-nowrap">{{ timeAgo(notif.created_at) }}</span>
                <div
                    v-if="!notif.is_read"
                    class="w-2 h-2 rounded-full bg-primary flex-shrink-0 animate-pulse-subtle"
                ></div>
              </div>
            </div>
            <p v-if="notif.message" class="text-sm text-text-secondary mt-1.5 leading-relaxed line-clamp-2">{{ notif.message }}</p>
          </div>

          <div
              class="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none"
              :class="notif.is_read ? 'bg-black/[0.02] dark:bg-white/[0.02]' : 'bg-primary/[0.02]'"
          ></div>
        </div>
      </TransitionGroup>

      <div v-if="loading" class="flex items-center justify-center py-8">
        <svg class="animate-spin h-6 w-6 text-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      </div>

      <div v-if="hasMore && !loading" class="text-center mt-8">
        <button
            @click="loadMore"
            class="px-8 py-2.5 text-sm font-medium text-primary bg-primary/10 hover:bg-primary/20 rounded-xl transition-all duration-200"
        >
          Load more
        </button>
      </div>
    </template>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { getNotifications, markAllNotificationsAsRead, markNotificationAsRead } from '@/services/api.js'
import { useRouter } from 'vue-router'
import { useBrowserNotifications } from '@/composables/useBrowserNotifications'

const router = useRouter()
const browserNotif = useBrowserNotifications()

const enableDesktopNotifs = async () => {
  await browserNotif.requestPermission()
}
const loading = ref(false)
const notifications = ref([])
const currentPage = ref(1)
const lastPage = ref(1)
const totalCount = ref(0)
const unreadCount = ref(0)

const hasMore = computed(() => currentPage.value < lastPage.value)
const hasUnread = computed(() => notifications.value.some(n => !n.is_read))

const typeColors = {
  admin_reply: { bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-600 dark:text-blue-400' },
  new_message: { bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-600 dark:text-blue-400' },
  order_status: { bg: 'bg-amber-100 dark:bg-amber-900/30', text: 'text-amber-600 dark:text-amber-400' },
  order_completed: { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-600 dark:text-green-400' },
  payment_received: { bg: 'bg-emerald-100 dark:bg-emerald-900/30', text: 'text-emerald-600 dark:text-emerald-400' },
  chat_disabled: { bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-600 dark:text-red-400' },
  chat_enabled: { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-600 dark:text-green-400' },
}

const fetch = async (page = 1) => {
  loading.value = true
  try {
    const result = await getNotifications(page)
    if (page === 1) {
      notifications.value = result.data || []
    } else {
      notifications.value = [...notifications.value, ...(result.data || [])]
    }
    totalCount.value = result.meta?.total || 0
    unreadCount.value = result.unread_count || 0
    currentPage.value = result.meta?.current_page || 1
    lastPage.value = result.meta?.last_page || 1
  } catch {
    // silent
  } finally {
    loading.value = false
  }
}

const loadMore = () => {
  if (hasMore.value) fetch(currentPage.value + 1)
}

const handleClick = async (notif) => {
  if (!notif.is_read) {
    await markNotificationAsRead(notif.id)
    notif.is_read = true
    unreadCount.value = Math.max(0, unreadCount.value - 1)
  }
  if (notif.action_url) {
    router.push(notif.action_url)
  }
}

const markAllRead = async () => {
  try {
    await markAllNotificationsAsRead()
    notifications.value.forEach(n => (n.is_read = true))
    unreadCount.value = 0
  } catch {
    // silent
  }
}

const timeAgo = (dateStr) => {
  if (!dateStr) return ''
  const now = new Date()
  const date = new Date(dateStr)
  const diffMs = now - date
  const diffSec = Math.floor(diffMs / 1000)
  const diffMin = Math.floor(diffSec / 60)
  const diffHour = Math.floor(diffMin / 60)
  const diffDay = Math.floor(diffHour / 24)

  if (diffSec < 60) return 'Just now'
  if (diffMin < 60) return `${diffMin}m ago`
  if (diffHour < 24) return `${diffHour}h ago`
  if (diffDay < 7) return `${diffDay}d ago`
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

onMounted(() => fetch())
</script>

<style scoped>
.notif-list-enter-active {
  transition: all 0.3s ease;
}
.notif-list-leave-active {
  transition: all 0.2s ease;
}
.notif-list-enter-from {
  opacity: 0;
  transform: translateY(12px);
}
.notif-list-leave-to {
  opacity: 0;
  transform: translateX(-12px);
}
.notif-list-move {
  transition: transform 0.3s ease;
}

.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.animate-pulse-subtle {
  animation: pulse-subtle 2s infinite;
}

@keyframes pulse-subtle {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}
</style>
